<!-- 
  @component Progressively Loading Image 

  Image that loads with a blurred version first, then the full image.
  Good for large images that may take a while to load, or for lazy loading.

  @note passes-through all the props to the `<Image />` component
  @warning won't work with external images, only CDN images. Pass a `blurSrc` for that.
-->
<script lang="ts" context="module">
  type OwnProps = {
    /**
     * the image source, generates a srcset and a blurry preview image
     * that will appear on load/scroll-into-view
     *
     * - `Api_Image` - pass an `Api_Image` object to use a blurhash and original.url
     * - `String` - pass a `String` to use server image resizer
     * - if dev environment, srcset wont generate for staging assets
     */
    src: Api_Image | string | undefined | null;
    alt: string;
    blur?: 'cover' | 'contain' | false;
    blurOpacity?: number;
    blurSrc?: string | undefined | null;
    blurHash?: string | undefined | null;
    threshold?: number;
    tabindex?: number | null;
    rounded?: Rounded;
    el?: HTMLImageElement | null;
    wrapEl?: HTMLDivElement | null;
    wrapClass?: string | null;
  };
</script>

<script lang="ts">
  import type { Api_Image } from '$lib/types/api/graphql';
  import type { Rounded } from '$mds/types';
  import { onMount } from 'svelte';
  import { observe } from '$lib/directives/observer';
  import { getBlurImage } from './image';
  import { createPngDataUri } from 'unlazy/blurhash';
  import Image from './Image.svelte';

  type $$Props = OwnProps & Omit<Image['$$prop_def'], 'src'>;

  export let src: Api_Image | string | undefined | null = null;
  export let el: HTMLImageElement | null = null;
  export let wrapEl: HTMLDivElement | null = null;
  /** classes to apply to the image container */
  export let wrapClass: string | null = null;
  /** give the blur image a cover or contain effect, false to disable blur */
  export let blur: 'cover' | 'contain' | false = 'cover';
  /** the opacity of the blur image while it is loading */
  export let blurOpacity: number = 1;
  /** if the image isn't from Metafy, then we can provide a blur src (eg: https://image.mux.com/X/thumbnail.jpg?width=12)*/
  export let blurSrc: string | undefined | null = null;
  /** if the image has a blurhash, then we can provide it here to use it instead of resizer */
  export let blurHash: string | undefined | null = '';
  /** Intersection threshold for the element to be considered in-view */
  export let threshold = 0.2;
  /** use tabindex to make it keyboard accessible */
  export let tabindex: number | null = null;
  /** use `rounded` over `class` to allow blurred image to match and not overflow */
  export let rounded: Rounded = '';
  export let alt: string;

  const positions = ['absolute', 'fixed', 'relative', 'sticky', 'static'];

  let loaded = false;
  let mounted = false;
  let inview = false;
  let reveal = false;
  $: isApiImage = typeof src === 'object' && src?.__typename === 'API_Image';
  $: apiImage = isApiImage ? (src as Api_Image) : null;
  $: imageUrl = ((isApiImage ? apiImage?.original?.url : src) ?? '') as string;

  onMount(() => {
    mounted = true;
    if (el?.complete) {
      loaded = true;
    }
  });

  let bgBlur = '';
  $: if (blurSrc) {
    bgBlur = `background-image: url(${blurSrc});`;
  } else if (blurHash) {
    const pngDataUri = createPngDataUri(blurHash || '');
    bgBlur = `background-image: url(${pngDataUri});`;
  } else if (isApiImage && apiImage?.blurhash) {
    const pngDataUri = createPngDataUri(apiImage?.blurhash || '', {
      ratio: (apiImage?.original?.width ?? 1) / (apiImage?.original?.height ?? 1),
      size: 100
    });
    bgBlur = `background-image: url(${pngDataUri});`;
  } else {
    // remember; this won't load on localhost as there's no CORS to resizer
    const resizedImage = imageUrl ? getBlurImage(imageUrl, 12) : '';
    bgBlur = `background-image: url(${resizedImage});`;
  }

  $: if (loaded && inview) {
    requestAnimationFrame(() => {
      reveal = true;
    });
  }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
  role={tabindex !== null ? 'button' : 'image'}
  {tabindex}
  bind:this={wrapEl}
  class="progressive-image {wrapClass ?? ''} motion-safe:animate-fade-in-slow animate-fill-both transform-gpu {rounded}"
  class:overflow-hidden={!wrapClass?.includes('overflow-')}
  class:relative={!wrapClass?.split(' ').some(c => positions.includes(c))}
  class:reveal
  class:mounted
  use:observe={{ threshold }}
  on:enterview={() => (inview = true)}
  on:click
  on:keyup
  on:keydown
  on:pointerup
  on:pointerdown
  on:focus
  on:blur
>
  <Image
    bind:el
    src={isApiImage ? apiImage?.original?.url : imageUrl}
    on:load
    on:load={() => (loaded = true)}
    on:error
    on:click
    {alt}
    {rounded}
    {...$$restProps}
  />
  {#if blur}
    <div
      class="lqip -z-1 absolute inset-0 bg-center bg-no-repeat blur-sm transition-[visibility,opacity] duration-1000 {rounded} transform-gpu overflow-hidden"
      class:bg-cover={blur === 'cover'}
      class:bg-contain={blur === 'contain'}
      style="{bgBlur ?? ''} {`opacity: ${blurOpacity}`}"
    ></div>
  {/if}
  <slot name="gradient" />
</div>

<style lang="postcss">
  .progressive-image :global(img) {
    @apply invisible opacity-0;
    @apply ease-smooth-out will-change-[opacity] motion-safe:transition-all;
  }
  .progressive-image.mounted :global(img) {
    @apply invisible opacity-0;
    transition-duration: var(--reveal-duration, 1s);
  }
  .progressive-image.mounted.reveal :global(img) {
    @apply opacity-100;
    visibility: inherit;
  }
  .progressive-image.mounted.reveal .lqip {
    /* 
      hide the blur image after the image is loaded
      so that the browser is no-longer trying to render.
    */
    @apply invisible !opacity-0; /* need !important to override {blurOpacity} */
    transition-delay: var(--reveal-duration, 1s);
  }
</style>
